import { Descriptions, Grid, Tabs } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../../shared/permissions';
import { type CtsModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/CtsModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import CtsModuleActions from './CtsModuleActions';
import CtsModuleInnerPage from './CtsModuleInnerPage';
import { useCtsSettingsDrawer } from './CtsSetting';
import { HeaderTabs } from './types';

export type CtsModulePageProps = {
    module: CtsModuleWithPermissionsSpecsFragment;
};

const CtsModulePage = ({ module }: CtsModulePageProps) => {
    const { t } = useTranslation(['ctsModuleDetails']);

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });

    const addCtsSettingsDrawer = useCtsSettingsDrawer(module.id, module.company);
    const screens = Grid.useBreakpoint();

    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(HeaderTabs.MainDetails);

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('ctsModuleDetails:descriptions.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('ctsModuleDetails:descriptions.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    const availableTabs = useMemo(
        () => [
            {
                label: t('ctsModuleDetails:tabs.mainDetails.title'),
                key: HeaderTabs.MainDetails,
                show: true,
            },
            {
                label: t('ctsModuleDetails:tabs.authorization.title'),
                key: HeaderTabs.Authorization,
                show: true,
            },
        ],
        [t]
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={
                <CtsModuleActions
                    module={module}
                    openCtsSettingDrawer={addCtsSettingsDrawer.open}
                    permissions={permissions}
                />
            }
            header={{
                footer: (
                    <Tabs
                        activeKey={activeTab}
                        items={availableTabs.filter(tabs => tabs.show)}
                        onChange={(activeKey: HeaderTabs) => setActiveTab(activeKey)}
                    />
                ),
            }}
            onBack={() => navigate(`/admin/system/modules`)}
            title={screens.md ? t('ctsModuleDetails:title', { name: module.displayName }) : module.displayName}
        >
            {addCtsSettingsDrawer.render()}
            <CtsModuleInnerPage
                activeTab={activeTab}
                module={module}
                openCtsSettingDrawer={addCtsSettingsDrawer.open}
                permissions={permissions}
            />
        </ConsolePageWithHeader>
    );
};

export default CtsModulePage;
